import { useTheme } from "@emotion/react";
import {
  Backdrop,
  Box,
  CircularProgress,
  Grid2,
  Typography,
  useMediaQuery,
} from "@mui/material";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import AuthLayout from "../../layout/AuthLayout";
import {
  CustomInput,
  CustomPasswordInput,
} from "../../components/Common/CustomInputs";
import {
  EmailOutlined,
  LoginOutlined,
  PersonOutlined,
} from "@mui/icons-material";
import { CustomBtn } from "../../components/Common/CustomBtn";
import axios from "axios";
import { getUserById } from "../../redux/actions/user.dispatcher";
import { useDispatch } from "react-redux";
import { ErrorSnackbar } from "../../components/Common/CustomSnackbar";
import { BASE_URL } from "../../utils/config/config";

const Register = () => {
  const dispatch = useDispatch();

  const theme = useTheme();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const mediumScreen = useMediaQuery(theme.breakpoints.down("md"));

  const [loading, setLoading] = useState(false);
  const [errorState, setErrorState] = useState({
    error: false,
    errorMsg: "",
  });

  const handleRegister = async () => {
    setErrorState({ error: false, errorMsg: "" });
    if (!email || !password || !firstName || !confirmPassword) {
      setErrorState({
        error: true,
        errorMsg: "Please fill all the fields",
      });
      return;
    }

    if (password.length < 8) {
      setErrorState({
        error: true,
        errorMsg: "Password must be at least 8 characters",
      });
      return;
    }

    if (password !== confirmPassword) {
      setErrorState({
        error: true,
        errorMsg: "Passwords do not match",
      });
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(`${BASE_URL}user/register`, {
        email: email.trim().toLowerCase(),
        password,
        firstName,
        lastName,
        role: "user",
      });
      if (response) {
        localStorage.setItem("token", response.data.accessToken);
        dispatch(getUserById());
        navigate("/home");
      }
    } catch (error) {
      setErrorState({
        error: true,
        errorMsg: error.response.data.message,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout>
      <Box sx={{ px: 4 }}>
        <Typography
          sx={{
            fontFamily: "'Gotham', sans-serif",
            fontSize: "50px",
            color: theme.palette.text.primary,
          }}
        >
          Register
        </Typography>
        <Typography
          sx={{
            fontFamily: "'Quicksand', sans-serif",
            fontSize: "22px",
            color: theme.palette.text.primary,
          }}
        >
          Create an account to get started.
        </Typography>

        <Box
          sx={{ my: 5 }}
          onKeyDown={(e) => e.key === "Enter" && handleRegister()}
        >
          <Grid2 container spacing={1}>
            <Grid2 item size={{ xs: 6 }}>
              <CustomInput
                placeholder="First Name"
                value={firstName}
                setValue={setFirstName}
                Icon={PersonOutlined}
                width={"100%"}
              />
            </Grid2>
            <Grid2 item size={{ xs: 6 }}>
              <CustomInput
                placeholder="Last Name"
                value={lastName}
                setValue={setLastName}
                Icon={PersonOutlined}
                width={"100%"}
              />
            </Grid2>
            <Grid2 item size={{ xs: 12 }}>
              <CustomInput
                placeholder="Email"
                value={email}
                setValue={setEmail}
                Icon={EmailOutlined}
                width={"100%"}
              />
            </Grid2>
            <Grid2 item size={{ xs: 6 }}>
              <CustomPasswordInput
                placeholder="Password"
                value={password}
                setValue={setPassword}
                width={"100%"}
              />
            </Grid2>
            <Grid2 item size={{ xs: 6 }}>
              <CustomPasswordInput
                placeholder="Confirm Password"
                value={confirmPassword}
                setValue={setConfirmPassword}
                width={"100%"}
              />
            </Grid2>
          </Grid2>
        </Box>

        <CustomBtn
          text="Register"
          Icon={LoginOutlined}
          width={mediumScreen ? "50%" : "30%"}
          onClick={handleRegister}
        />
      </Box>
      <Box sx={{ px: 4 }}>
        <Typography
          onClick={() => navigate("/login")}
          sx={{
            fontSize: "18px",
            fontFamily: "'Quicksand', sans-serif",
            color: theme.palette.text.primary,
            fontWeight: "400",
          }}
        >
          Already have an account?{" "}
          <Typography
            component={"span"}
            sx={{
              fontSize: "18px",
              fontFamily: "'Quicksand', sans-serif",
              color: theme.palette.text.primary,
              fontWeight: "600",
              display: "inline",
              cursor: "pointer",
              textDecorationLine: "underline",
            }}
          >
            Login
          </Typography>
        </Typography>
      </Box>

      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <ErrorSnackbar
        error={errorState.error}
        handleCloseError={() => setErrorState({ error: false, errorMsg: "" })}
        errorMsg={errorState.errorMsg}
      />
    </AuthLayout>
  );
};

export default Register;
